"use client";

import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { AppDispatch } from "../../../../../../app/store";
import { RootState } from "../../../../../../app/rootReducer";
import {
  fetchCompanyProfileAction,
  updateCompanyProfileAction,
} from "../../slices/companyProfileSlice";
import { useNotification } from "../../../../../../context/NotificationContext";
import {
  companyProfileSchema,
  CompanyProfileFormData,
} from "../../schemas/settingsSchemas";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@headlessui/react";
import Select from "react-select";
import countryList from "react-select-country-list";
import { allCountries } from "country-region-data";
import clsx from "clsx";

interface OptionType {
  label: string;
  value: string;
}

const CompanyProfile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showNotification } = useNotification();

  const { companyProfile, loading } = useSelector(
    (state: RootState) => state.companyProfile,
  );

  const countryOptions = useMemo(() => countryList().getData(), []);

  const form = useForm<CompanyProfileFormData>({
    resolver: zodResolver(companyProfileSchema),
    defaultValues: {
      companyName: "",
      companyEmail: "",
      websiteUrl: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      country: "US",
      zipCode: "",
      isMailingAddressSame: true,
      mailingAddressLine1: "",
      mailingAddressLine2: "",
      mailingState: "",
      mailingCountry: "US",
      mailingZipCode: "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    dispatch(fetchCompanyProfileAction());
  }, [dispatch]);

  useEffect(() => {
    if (companyProfile) {
      form.reset({
        companyName: companyProfile.companyName || "",
        companyEmail: companyProfile.companyEmail || "",
        websiteUrl: companyProfile.websiteUrl || "",
        addressLine1: companyProfile.addressLine1 || "",
        addressLine2: companyProfile.addressLine2 || "",
        city: companyProfile.city || "",
        state: companyProfile.state || "",
        country: companyProfile.country || "US",
        zipCode: companyProfile.zipCode || "",
        isMailingAddressSame: companyProfile.isMailingAddressSame ?? true,
        mailingAddressLine1: companyProfile.mailingAddressLine1 || "",
        mailingAddressLine2: companyProfile.mailingAddressLine2 || "",
        mailingState: companyProfile.mailingState || "",
        mailingCountry: companyProfile.mailingCountry || "US",
        mailingZipCode: companyProfile.mailingZipCode || "",
      });
    }
  }, [companyProfile, form]);

  const onSubmit = (data: CompanyProfileFormData) => {
    // Convert empty strings to null for API
    const updateData = {
      companyName: data.companyName,
      companyEmail: data.companyEmail || null,
      websiteUrl: data.websiteUrl || null,
      addressLine1: data.addressLine1 || null,
      addressLine2: data.addressLine2 || null,
      city: data.city || null,
      state: data.state || null,
      country: data.country || null,
      zipCode: data.zipCode || null,
      isMailingAddressSame: data.isMailingAddressSame,
      mailingAddressLine1: data.isMailingAddressSame
        ? null
        : data.mailingAddressLine1 || null,
      mailingAddressLine2: data.isMailingAddressSame
        ? null
        : data.mailingAddressLine2 || null,
      mailingState: data.isMailingAddressSame
        ? null
        : data.mailingState || null,
      mailingCountry: data.isMailingAddressSame
        ? null
        : data.mailingCountry || null,
      mailingZipCode: data.isMailingAddressSame
        ? null
        : data.mailingZipCode || null,
    };

    dispatch(
      updateCompanyProfileAction(updateData, () => {
        showNotification(
          "Successfully updated company profile!",
          "Your company profile information has been updated.",
          "success",
        );
      }),
    );
  };

  const handleCancel = () => {
    if (companyProfile) {
      form.reset({
        companyName: companyProfile.companyName || "",
        companyEmail: companyProfile.companyEmail || "",
        websiteUrl: companyProfile.websiteUrl || "",
        addressLine1: companyProfile.addressLine1 || "",
        addressLine2: companyProfile.addressLine2 || "",
        city: companyProfile.city || "",
        state: companyProfile.state || "",
        country: companyProfile.country || "US",
        zipCode: companyProfile.zipCode || "",
        isMailingAddressSame: companyProfile.isMailingAddressSame ?? true,
        mailingAddressLine1: companyProfile.mailingAddressLine1 || "",
        mailingAddressLine2: companyProfile.mailingAddressLine2 || "",
        mailingState: companyProfile.mailingState || "",
        mailingCountry: companyProfile.mailingCountry || "US",
        mailingZipCode: companyProfile.mailingZipCode || "",
      });
    }
  };

  // Get state options based on selected country
  const country = form.watch("country");
  const stateOptions = useMemo(() => {
    if (!country) return [];
    const countryData = allCountries.find((c) => c[1] === country);
    if (countryData && countryData[2] && countryData[2].length > 0) {
      return countryData[2].map((region) => ({
        value: region[1],
        label: region[0],
      }));
    }
    return [];
  }, [country]);

  // Get mailing state options based on selected mailing country
  const mailingCountry = form.watch("mailingCountry");
  const mailingStateOptions = useMemo(() => {
    if (!mailingCountry) return [];
    const countryData = allCountries.find((c) => c[1] === mailingCountry);
    if (countryData && countryData[2] && countryData[2].length > 0) {
      return countryData[2].map((region) => ({
        value: region[1],
        label: region[0],
      }));
    }
    return [];
  }, [mailingCountry]);

  const isMailingAddressSame = form.watch("isMailingAddressSame");

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company Profile</CardTitle>
        <CardDescription>
          Manage your company information and settings.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
            {/* Company Name */}
            <FormField
              control={form.control}
              name="companyName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Name</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Company Email */}
            <FormField
              control={form.control}
              name="companyEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Email</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={loading}
                      type="email"
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Website URL */}
            <FormField
              control={form.control}
              name="websiteUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Website URL</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={loading}
                      placeholder="https://www.company.com"
                      type="url"
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Address Line 1 */}
            <FormField
              control={form.control}
              name="addressLine1"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address Line 1</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={loading}
                      placeholder="8111 Mainland Dr."
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Address Line 2 */}
            <FormField
              control={form.control}
              name="addressLine2"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address Line 2</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={loading}
                      placeholder="Suite 100"
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              {/* City */}
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={loading}
                        placeholder="San Antonio"
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Country */}
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country</FormLabel>
                    <FormControl>
                      <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isDisabled={loading}
                        onChange={(option) => {
                          field.onChange(option?.value || "");
                          // Reset state when country changes
                          form.setValue("state", "");
                        }}
                        options={countryOptions}
                        value={
                          countryOptions.find(
                            (option) => option.value === field.value,
                          ) || null
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* State */}
              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State / Province</FormLabel>
                    <FormControl>
                      {stateOptions.length > 0 ? (
                        <Select
                          className="react-select-container"
                          classNamePrefix="react-select"
                          isDisabled={loading}
                          onChange={(option) =>
                            field.onChange(option?.value || "")
                          }
                          options={stateOptions}
                          value={
                            stateOptions.find(
                              (option) => option.value === field.value,
                            ) || null
                          }
                        />
                      ) : (
                        <Input
                          {...field}
                          disabled={loading}
                          placeholder="State / Province"
                          value={field.value || ""}
                        />
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Zip Code */}
            <FormField
              control={form.control}
              name="zipCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ZIP / Postal Code</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={loading}
                      placeholder="78240"
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Mailing Address Same Toggle */}
            <FormField
              control={form.control}
              name="isMailingAddressSame"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mailing Address</FormLabel>
                  <div className="flex items-center mt-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        className={clsx(
                          field.value
                            ? "bg-indigo-600"
                            : "bg-gray-200 dark:bg-slate-600",
                          "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2",
                        )}
                        disabled={loading}
                        onChange={field.onChange}
                      >
                        <span
                          className={clsx(
                            field.value ? "translate-x-6" : "translate-x-1",
                            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                          )}
                        />
                      </Switch>
                    </FormControl>
                    <span className="ml-3 text-sm text-gray-600 dark:text-gray-300">
                      {field.value
                        ? "Same as Address"
                        : "Different Mailing Address"}
                    </span>
                  </div>
                </FormItem>
              )}
            />

            {/* Conditional Mailing Address Fields */}
            {!isMailingAddressSame && (
              <div className="space-y-6 pt-4 border-t dark:border-slate-700">
                {/* Mailing Address Line 1 */}
                <FormField
                  control={form.control}
                  name="mailingAddressLine1"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mailing Address Line 1</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={loading}
                          placeholder="7224 Eckhert Rd"
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Mailing Address Line 2 */}
                <FormField
                  control={form.control}
                  name="mailingAddressLine2"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mailing Address Line 2</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={loading}
                          placeholder="Suite 200"
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                  {/* Mailing Country */}
                  <FormField
                    control={form.control}
                    name="mailingCountry"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mailing Country</FormLabel>
                        <FormControl>
                          <Select
                            className="react-select-container"
                            classNamePrefix="react-select"
                            isDisabled={loading}
                            onChange={(option) => {
                              field.onChange(option?.value || "");
                              // Reset mailing state when mailing country changes
                              form.setValue("mailingState", "");
                            }}
                            options={countryOptions}
                            value={
                              countryOptions.find(
                                (option) => option.value === field.value,
                              ) || null
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Mailing State */}
                  <FormField
                    control={form.control}
                    name="mailingState"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mailing State / Province</FormLabel>
                        <FormControl>
                          {mailingStateOptions.length > 0 ? (
                            <Select
                              className="react-select-container"
                              classNamePrefix="react-select"
                              isDisabled={loading}
                              onChange={(option) =>
                                field.onChange(option?.value || "")
                              }
                              options={mailingStateOptions}
                              value={
                                mailingStateOptions.find(
                                  (option) => option.value === field.value,
                                ) || null
                              }
                            />
                          ) : (
                            <Input
                              {...field}
                              disabled={loading}
                              placeholder="State / Province"
                              value={field.value || ""}
                            />
                          )}
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Mailing Zip Code */}
                  <FormField
                    control={form.control}
                    name="mailingZipCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mailing ZIP / Postal Code</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            disabled={loading}
                            placeholder="78238-1244"
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-4">
              <Button
                disabled={loading}
                onClick={handleCancel}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button disabled={loading} type="submit">
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default CompanyProfile;
